import React, { useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { Layout } from '@/client/layouts/layout';
import { API_BASE_URL } from '@/client/config';
import { getMonthName, getShortMonthName, formatDuration } from '@/client/lib/date';
import { parseString, buildSearchParams } from '@/client/lib/searchParams';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { downloadCSV } from '@/client/lib/csv';
import { ChevronDown, Download, TrendingUp } from 'lucide-react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import type { TrendsResponse, TrendDataPoint, ClientResponse } from '@shared/types';

import { Button } from '@/client/components/ui/button';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { ClientSelector } from '@/client/components/client-selector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/client/components/ui/chart';

const YEAR_RANGE_OPTIONS = [
  { value: '1', label: 'Last 1 year' },
  { value: '2', label: 'Last 2 years' },
  { value: '3', label: 'Last 3 years' },
  { value: '4', label: 'Last 4 years' },
  { value: '5', label: 'Last 5 years' },
  { value: '6', label: 'Last 6 years' },
  { value: '7', label: 'Last 7 years' },
  { value: '8', label: 'Last 8 years' },
  { value: '9', label: 'Last 9 years' },
  { value: '10', label: 'Last 10 years' },
];

// Chart colors - monochromatic blue gradient (light to dark)
// CSS variables for 1-5, then continue gradient for 6-10
const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'oklch(0.40 0.12 250)',
  'oklch(0.35 0.11 250)',
  'oklch(0.30 0.10 250)',
  'oklch(0.25 0.09 250)',
  'oklch(0.20 0.08 250)',
];

const VALID_YEAR_VALUES = YEAR_RANGE_OPTIONS.map(o => o.value);

type TrendsSearch = {
  clientID?: string;
  years?: string;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/trends',
  validateSearch: (search: Record<string, unknown>): TrendsSearch => ({
    clientID: parseString(search.clientID),
    years: VALID_YEAR_VALUES.includes(search.years as string)
      ? (search.years as string)
      : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: TrendsComponent,
});

// Transform API data to chart format (grouped by month, with year columns)
function transformToChartData(data: TrendDataPoint[], years: number[]): Record<string, unknown>[] {
  const chartData: Record<string, unknown>[] = [];

  // Initialize all 12 months
  for (let month = 1; month <= 12; month++) {
    const row: Record<string, unknown> = { month: getShortMonthName(month) };
    years.forEach(year => {
      row[year.toString()] = 0;
    });
    chartData.push(row);
  }

  // Fill in actual data
  data.forEach(point => {
    const monthIndex = point.month - 1;
    if (chartData[monthIndex]) {
      chartData[monthIndex][point.year.toString()] = point.eventCount;
    }
  });

  return chartData;
}

function TrendsComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { clientID: selectedClientId = '', years: selectedYears = '2' } = useSearch({ from: '/events/trends' });

  // Persist search params across navigation
  usePersistedSearch('/events/trends', { clientID: undefined, years: '2' });

  // State
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);
  const [trendsData, setTrendsData] = useState<TrendsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  // Setters that update URL
  const setSelectedYears = (value: string) => {
    navigate({
      to: '/events/trends',
      search: buildSearchParams({ clientID: selectedClientId || undefined, years: value !== '2' ? value : undefined }),
    });
  };

  // Handle client change from ClientSelector
  const handleClientChange = (clientId: string, client: ClientResponse | null) => {
    setSelectedClient(client);
    navigate({
      to: '/events/trends',
      search: buildSearchParams({ clientID: clientId || undefined, years: selectedYears !== '2' ? selectedYears : undefined }),
    });
    setTrendsData(null);
    setConfirmPending(false);
  };

  // Get unique years from the data
  const uniqueYears = useMemo(() => {
    if (!trendsData) return [];
    const yearsSet = new Set<number>();
    trendsData.liveStreams.forEach(d => yearsSet.add(d.year));
    trendsData.vod.forEach(d => yearsSet.add(d.year));
    return Array.from(yearsSet).sort((a, b) => a - b); // Ascending: older years left, newer right
  }, [trendsData]);

  // Chart config based on years
  // Dynamically generate colors with good contrast: newest = lightest, oldest = darkest
  const chartConfig = useMemo((): ChartConfig => {
    const config: ChartConfig = {};
    const count = uniqueYears.length;

    // Generate evenly spaced colors from dark (oldest) to light (newest)
    // Lightness range: 0.40 (darkest/oldest) to 0.75 (lightest/newest)
    uniqueYears.forEach((year, index) => {
      const t = count === 1 ? 1 : index / (count - 1); // 0 = oldest, 1 = newest
      const lightness = 0.40 + t * 0.35; // 0.40 to 0.75
      config[year.toString()] = {
        label: year.toString(),
        color: `oklch(${lightness.toFixed(2)} 0.13 250)`,
      };
    });
    return config;
  }, [uniqueYears]);

  // Transform data for charts
  const liveStreamsChartData = useMemo(() => {
    if (!trendsData) return [];
    return transformToChartData(trendsData.liveStreams, uniqueYears);
  }, [trendsData, uniqueYears]);

  const vodChartData = useMemo(() => {
    if (!trendsData) return [];
    return transformToChartData(trendsData.vod, uniqueYears);
  }, [trendsData, uniqueYears]);

  const canGenerateReport = selectedClientId;

  const handleGenerateReport = async () => {
    if (!canGenerateReport) return;

    if (!confirmPending) {
      setConfirmPending(true);
      return;
    }

    setConfirmPending(false);
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE_URL}/api/events/trends?clientID=${selectedClientId}&years=${selectedYears}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch trends: ${response.statusText}`);
      }

      const data: TrendsResponse = await response.json();
      setTrendsData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!trendsData) return;

    const headers = ['Year', 'Month', 'Live Events', 'Live Total Duration', 'Live Avg Duration', 'VOD Events', 'VOD Total Duration', 'VOD Avg Duration'];

    // Create a map for quick lookup
    const liveMap = new Map<string, TrendDataPoint>();
    const vodMap = new Map<string, TrendDataPoint>();

    trendsData.liveStreams.forEach(d => liveMap.set(`${d.year}-${d.month}`, d));
    trendsData.vod.forEach(d => vodMap.set(`${d.year}-${d.month}`, d));

    const rows: string[][] = [];

    // Sort years descending, months ascending
    uniqueYears.forEach(year => {
      for (let month = 1; month <= 12; month++) {
        const live = liveMap.get(`${year}-${month}`);
        const vod = vodMap.get(`${year}-${month}`);

        if (live || vod) {
          rows.push([
            year.toString(),
            getMonthName(month),
            (live?.eventCount ?? 0).toString(),
            (live?.totalDurationMinutes ?? 0).toString(),
            (live?.avgDurationMinutes ?? 0).toString(),
            (vod?.eventCount ?? 0).toString(),
            (vod?.totalDurationMinutes ?? 0).toString(),
            (vod?.avgDurationMinutes ?? 0).toString(),
          ]);
        }
      }
    });

    const clientName = selectedClient?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'client';
    downloadCSV(`trends_${clientName}_${selectedYears}yr.csv`, headers, rows);
  };

  const selectedRangeLabel = YEAR_RANGE_OPTIONS.find(o => o.value === selectedYears)?.label || '';

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-semibold">Yearly Trends</h3>
          <CacheTimestamp cachedAt={trendsData?.cacheInfo?.cachedAt ?? null} />
        </div>

        {/* Controls Section */}
        <div className="border rounded-lg p-4 space-y-4 bg-card">
          {/* Range & Client Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Range & Client</span>
            <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
              {/* Year Range Select */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-10 min-w-[160px] justify-between font-normal">
                    {selectedRangeLabel}
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
                  {YEAR_RANGE_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => {
                        setSelectedYears(option.value);
                        setConfirmPending(false);
                      }}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Client Selector */}
              <ClientSelector
                value={selectedClientId}
                onChange={handleClientChange}
                className="min-w-[250px]"
                label=""
              />

              {/* Generate Report Button */}
              <Button
                onClick={handleGenerateReport}
                disabled={!canGenerateReport || loading}
                variant={confirmPending ? 'destructive' : 'default'}
                className="min-w-[175px]"
              >
                {loading ? (
                  'Loading...'
                ) : confirmPending ? (
                  'Click to Confirm'
                ) : (
                  <>
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>

              <ResetFiltersButton
                routePath="/events/trends"
                defaultValues={{ clientID: undefined, years: '2' }}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded">
            <p className="m-0 text-red-800 font-medium">Error: {error}</p>
          </div>
        )}

        {/* Results Section */}
        {trendsData && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                Trends for Client {trendsData.clientID} - Last {trendsData.years} year{trendsData.years > 1 ? 's' : ''}
              </div>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>

            {/* Live Streams Chart */}
            {trendsData.liveStreams.length > 0 && (
              <div className="space-y-4 bg-card rounded-lg p-4">
                <h4 className="text-lg font-semibold">Live Streams - Events by Month</h4>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={liveStreamsChartData} accessibilityLayer>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    {uniqueYears.map((year) => (
                      <Bar
                        key={year}
                        dataKey={year.toString()}
                        fill={`var(--color-${year})`}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              </div>
            )}

            {/* VOD Chart */}
            {trendsData.vod.length > 0 && (
              <div className="space-y-4 bg-card rounded-lg p-4">
                <h4 className="text-lg font-semibold">VOD - Events by Month</h4>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={vodChartData} accessibilityLayer>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    {uniqueYears.map((year) => (
                      <Bar
                        key={year}
                        dataKey={year.toString()}
                        fill={`var(--color-${year})`}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              </div>
            )}

            {/* Live Streams Table */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Live Streams Data</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Events</TableHead>
                      <TableHead className="text-right">Total Duration</TableHead>
                      <TableHead className="text-right">Avg Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trendsData.liveStreams.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No live stream data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      uniqueYears.map(year => (
                        <React.Fragment key={year}>
                          {trendsData.liveStreams
                            .filter(d => d.year === year)
                            .sort((a, b) => a.month - b.month)
                            .map((row, idx) => (
                              <TableRow key={`${year}-${row.month}`}>
                                {idx === 0 ? (
                                  <TableCell className="font-semibold" rowSpan={trendsData.liveStreams.filter(d => d.year === year).length}>
                                    {year}
                                  </TableCell>
                                ) : null}
                                <TableCell>{getMonthName(row.month)}</TableCell>
                                <TableCell className="text-right">{row.eventCount}</TableCell>
                                <TableCell className="text-right">{formatDuration(row.totalDurationMinutes)}</TableCell>
                                <TableCell className="text-right">{formatDuration(row.avgDurationMinutes)}</TableCell>
                              </TableRow>
                            ))}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* VOD Table */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">VOD Data</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Events</TableHead>
                      <TableHead className="text-right">Total Duration</TableHead>
                      <TableHead className="text-right">Avg Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trendsData.vod.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No VOD data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      uniqueYears.map(year => (
                        <React.Fragment key={year}>
                          {trendsData.vod
                            .filter(d => d.year === year)
                            .sort((a, b) => a.month - b.month)
                            .map((row, idx) => (
                              <TableRow key={`${year}-${row.month}`}>
                                {idx === 0 ? (
                                  <TableCell className="font-semibold" rowSpan={trendsData.vod.filter(d => d.year === year).length}>
                                    {year}
                                  </TableCell>
                                ) : null}
                                <TableCell>{getMonthName(row.month)}</TableCell>
                                <TableCell className="text-right">{row.eventCount}</TableCell>
                                <TableCell className="text-right">{formatDuration(row.totalDurationMinutes)}</TableCell>
                                <TableCell className="text-right">{formatDuration(row.avgDurationMinutes)}</TableCell>
                              </TableRow>
                            ))}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
